import { VirtualRoomArea } from './VirtualRoomAreaService'

export interface VirtualRoomAreaBorderRendererOptions {
  tileSize: number
  borderColor: string
  borderWidth: number
  borderOpacity: number
  depth: number
  enabled: boolean
}

export class VirtualRoomAreaBorderRenderer {
  private scene: Phaser.Scene | null = null
  private graphics: Map<string, Phaser.GameObjects.Graphics> = new Map() // Track graphics by area ID
  private options: VirtualRoomAreaBorderRendererOptions
  private isEnabled: boolean = false

  constructor(options: Partial<VirtualRoomAreaBorderRendererOptions> = {}) {
    this.options = {
      tileSize: 32,
      borderColor: '#ffffff',
      borderWidth: 2,
      borderOpacity: 0.5,
      depth: 50,
      enabled: true,
      ...options
    }
    this.isEnabled = this.options.enabled
  }

  /**
   * Set the Phaser scene for rendering
   */
  public setScene(scene: Phaser.Scene): void {
    this.scene = scene
  }

  /**
   * Update the renderer options
   */
  public updateOptions(options: Partial<VirtualRoomAreaBorderRendererOptions>): void {
    this.options = { ...this.options, ...options }
    if (options.enabled !== undefined) {
      this.isEnabled = options.enabled
    }
  }

  /**
   * Enable or disable border rendering
   */
  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled
    if (!enabled) {
      this.clearAllBorders()
    }
  }

  /**
   * Check if border rendering is enabled
   */
  public isBorderRenderingEnabled(): boolean {
    return this.isEnabled
  }

  /**
   * Clear all existing borders
   */
  public clearAllBorders(): void {
    this.graphics.forEach((graphics, areaId) => {
      if (graphics && graphics.destroy) {
        graphics.destroy()
      }
    })
    this.graphics.clear()

  }

  /**
   * Remove border graphics for a specific area
   */
  public clearAreaBorder(areaId: string): void {
    const graphics = this.graphics.get(areaId)
    if (graphics && graphics.destroy) {
      graphics.destroy()
    }
    this.graphics.delete(areaId)
  }

  /**
   * Paint borders for all areas
   */
  public paintAreaBorders(areas: VirtualRoomArea[]): void {
    if (!this.isEnabled || !this.scene) {
      return
    }

    // Clear existing borders
    this.clearAllBorders()

    if (areas.length === 0) {
      return
    }

    // Paint borders for each area
    areas.forEach(area => {
      this.paintSingleAreaBorder(area)
    })
  }

  /**
   * Paint a continuous border around a single area
   */
  public paintSingleAreaBorder(area: VirtualRoomArea): void {
    if (!this.isEnabled || !this.scene) {
      return
    }

    // Remove existing border for this area if it exists
    const existingGraphics = this.graphics.get(area.roomId)
    if (existingGraphics && existingGraphics.destroy) {
      existingGraphics.destroy()
      this.graphics.delete(area.roomId)
    }

    if (area.tiles.length === 0) {
      return
    }

    // Convert area color to number, fallback to white if parsing fails
    let borderColor: number
    try {
      borderColor = this.parseColor(area.color)
    } catch (error) {
      borderColor = 0xffffff
    }
    
    // Create a single graphics object for this area's border
    const borderGraphics = this.scene.add.graphics()
    borderGraphics.setDepth(this.options.depth)
    borderGraphics.lineStyle(this.options.borderWidth, borderColor, this.options.borderOpacity)
    
    // Create a set of all area tiles for quick lookup
    const tileSet = new Set(area.tiles.map(t => `${t.x},${t.y}`))
    
    // Find all edges that need borders (edges between area tiles and empty space)
    const edges: Array<{ x1: number; y1: number; x2: number; y2: number }> = []
    
    for (const tile of area.tiles) {
      const { x, y } = tile
      
      // Check each side of the tile explicitly
      // Top edge: check if tile above is empty
      if (!tileSet.has(`${x},${y - 1}`)) {
        edges.push({ x1: x, y1: y, x2: x + 1, y2: y })
      }
      
      // Bottom edge: check if tile below is empty
      if (!tileSet.has(`${x},${y + 1}`)) {
        edges.push({ x1: x, y1: y + 1, x2: x + 1, y2: y + 1 })
      }
      
      // Left edge: check if tile to the left is empty
      if (!tileSet.has(`${x - 1},${y}`)) {
        edges.push({ x1: x, y1: y, x2: x, y2: y + 1 })
      }
      
      // Right edge: check if tile to the right is empty
      if (!tileSet.has(`${x + 1},${y}`)) {
        edges.push({ x1: x + 1, y1: y, x2: x + 1, y2: y + 1 })
      }
    }
    
    // Draw all outer edges
    for (const edge of edges) {
      const pixelX1 = edge.x1 * this.options.tileSize
      const pixelY1 = edge.y1 * this.options.tileSize
      const pixelX2 = edge.x2 * this.options.tileSize
      const pixelY2 = edge.y2 * this.options.tileSize
      
      borderGraphics.beginPath()
      borderGraphics.moveTo(pixelX1, pixelY1)
      borderGraphics.lineTo(pixelX2, pixelY2)
      borderGraphics.strokePath()
    }
    
    // Store the graphics reference for this area
    this.graphics.set(area.roomId, borderGraphics)
    

  }

  /**
   * Get the current graphics objects
   */
  public getGraphics(): Map<string, Phaser.GameObjects.Graphics> {
    return this.graphics
  }

  /**
   * Check if borders are currently rendered
   */
  public hasBorders(): boolean {
    return this.graphics.size > 0
  }

  /**
   * Get the number of rendered borders
   */
  public getBorderCount(): number {
    return this.graphics.size
  }

  /**
   * Destroy the renderer and clean up resources
   */
  public destroy(): void {
    this.clearAllBorders()
    this.scene = null

  }

  /**
   * Parse color string to number
   */
  private parseColor(color: string): number {
    if (!color) return 0xffffff
    
    // Handle hex colors
    if (color.startsWith('#')) {
      return parseInt(color.replace('#', ''), 16)
    }
    
    // Handle HSL colors
    if (color.startsWith('hsl')) {
      // Simple HSL to RGB conversion (basic implementation)
      const hsl = color.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/)
      if (hsl) {
        const h = parseInt(hsl[1]) / 360
        const s = parseInt(hsl[2]) / 100
        const l = parseInt(hsl[3]) / 100
        
        const hue2rgb = (p: number, q: number, t: number) => {
          if (t < 0) t += 1
          if (t > 1) t -= 1
          if (t < 1/6) return p + (q - p) * 6 * t
          if (t < 1/2) return q
          if (t < 2/3) return p + (q - p) * (2/3 - t) * 6
          return p
        }
        
        let r, g, b
        if (s === 0) {
          r = g = b = l
        } else {
          const q = l < 0.5 ? l * (1 + s) : l + s - l * s
          const p = 2 * l - q
          r = hue2rgb(p, q, h + 1/3)
          g = hue2rgb(p, q, h)
          b = hue2rgb(p, q, h - 1/3)
        }
        
        return Phaser.Display.Color.GetColor(
          Math.round(r * 255),
          Math.round(g * 255),
          Math.round(b * 255)
        )
      }
    }
    
    // Handle RGB (r,g,b)
    if (color.includes(',')) {
      const rgb = color.split(',').map(Number)
      if (rgb.length >= 3) {
        return Phaser.Display.Color.GetColor(rgb[0], rgb[1], rgb[2])
      }
    }
    
    // Handle named colors
    const namedColors: { [key: string]: number } = {
      'red': 0xff0000,
      'green': 0x00ff00,
      'blue': 0x0000ff,
      'yellow': 0xffff00,
      'magenta': 0xff00ff,
      'cyan': 0x00ffff,
      'white': 0xffffff,
      'black': 0x000000,
      'orange': 0xff8800,
      'purple': 0x8800ff,
      'pink': 0xffc0cb,
      'brown': 0xa52a2a,
      'gray': 0x808080,
      'grey': 0x808080
    }
    
    const lowerColor = color.toLowerCase().trim()
    if (namedColors[lowerColor]) {
      return namedColors[lowerColor]
    }
    

    return 0xffffff
  }
}

// Export singleton instance
export const virtualRoomAreaBorderRenderer = new VirtualRoomAreaBorderRenderer() 